import type { InMemoryCache } from '@apollo/client';
import type { ReactNode } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import React, { createContext, useState, useCallback, useMemo, useRef } from 'react';
// import useReactRouter from 'use-react-router';
import { useHistory } from 'react-router-dom';

import { CREATE_MESSAGE } from '../graphql/CREATE_MESSAGE';
import { CREATE_ROOM } from '../graphql/CREATE_ROOM';
import { GET_ROOM } from '../graphql/GET_ROOM';
import { HAS_SINGLE_CHAT_HISTORY } from '../graphql/HAS_SINGLE_CHAT_HISTORY';
import { UPDATE_COFFEE_CHAT_ACCEPTANCE } from '../graphql/UPDATE_COFFEE_CHAT_ACCEPTANCE';
import { GET_CURRENT_USER_me } from '../graphql/types/GET_CURRENT_USER';
import { GET_ROOM_getRoom } from '../graphql/types/GET_ROOM';
import { GET_USER_getUser } from '../graphql/types/GET_USER';
import { UPDATE_READ_AT_MESSAGES } from '../graphql/UPDATE_READ_AT_MESSAGES';
import {
  GET_PAGINATED_ROOMS_getPaginatedRooms_members,
  GET_PAGINATED_ROOMS as GET_PAGINATED_ROOMS_RESPONSE
} from '../graphql/types/GET_PAGINATED_ROOMS';
import { GET_PAGINATED_ROOMS } from '../graphql/GET_PAGINATED_ROOMS';
import { CREATE_ROOM_createChatRoom } from '../graphql/types/CREATE_ROOM';
import { useRoomListsContext } from './roomListsContext';

interface IGetRoom {
  getRoom: GET_ROOM_getRoom;
}

const initContextValues = {
  actions: {
    getOtherUser: (
      _: GET_PAGINATED_ROOMS_getPaginatedRooms_members[]
    ): GET_PAGINATED_ROOMS_getPaginatedRooms_members | null => null,
    handleCreateMessage: (_: number, __: string, ___?: number) => Promise<void>,
    handleCreateRoom: (_: number[], __ = 'single') => Promise<void>,
    handleCreateCoffeeChatRoom: (_: number[]) => Promise<void>,
    handleLoadRoom: (_: string) => Promise<void>,
    handleReadAtMessages: (_: number[], __: number) => Promise<void>,
    handleSelectOtherUser: (_: GET_USER_getUser) => Promise<void>,
    loadMoreMessages: (_: string) => Promise<void>,
    handleAcceptCoffeeChat: (_: string) => Promise<boolean>
  },
  currentRoom: {
    called: false,
    data: {
      getRoom: {
        createdAt: null,
        id: undefined,
        members: [],
        messages: []
      }
    },
    error: null,
    loading: false,
    hasMore: true,
    offset: 0
  },
  currentUser: {},
  singleChatHistory: {
    called: false,
    data: {
      hasSingleChatHistory: false
    },
    error: null,
    loading: false,
    hasMore: true,
    offset: 0
  }
} as any;

export const MessageContext = createContext(initContextValues);

interface IMessageContextProviderProps {
  children: ReactNode;
  currentUser: GET_CURRENT_USER_me;
}

export const MessageContextProvider = (props: IMessageContextProviderProps) => {
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const MESSAGES_PER_PAGE = 10;
  const latestMessageIdRef = useRef<string | null>(null);

  const history = useHistory();
  const [selectedOtherUser, setSelectedOtherUser] =
    useState<GET_CURRENT_USER_me | null>(null);

  const { currentPage, roomsPerPage } = useRoomListsContext();

  const [
    hasSingleChatHistory,
    {
      called: singleChatHistoryCalled,
      error: singleChatHistoryError,
      data: singleChatHistoryData,
      loading: singleChatHistoryLoading
    }
  ] = useLazyQuery(HAS_SINGLE_CHAT_HISTORY, {
    onCompleted: async (d) => {
      if (!selectedOtherUser) {
        alert('Please select other user');
        return;
      }
      // If there are no room history, create a new room
      if (d.hasSingleChatHistory && !d.hasSingleChatHistory.hasRoomHistory) {
        await handleCreateRoom([Number(selectedOtherUser.id)]);
      }

      // If a room already exist, then redirect to the room URL.
      if (
        d.hasSingleChatHistory &&
        d.hasSingleChatHistory.hasRoomHistory &&
        d.hasSingleChatHistory.room
      ) {
        return redirectToRoom(d.hasSingleChatHistory.room.id);
      }
    }
  });

  const [createMessage] = useMutation(CREATE_MESSAGE);
  const [createRoom] = useMutation(CREATE_ROOM);
  const [updateReadAtMessages] = useMutation(UPDATE_READ_AT_MESSAGES);
  const [updateCoffeeChatAcceptance] = useMutation(UPDATE_COFFEE_CHAT_ACCEPTANCE);

  const [loadRoom, {
    called: roomCalled,
    error: roomError,
    data: roomData,
    loading: roomLoading,
    startPolling,
    stopPolling,
    fetchMore
  }] = useLazyQuery(GET_ROOM, {
    onCompleted: (data) => {
      if (!data) {
        console.error('No data received from GET_ROOM query');
        return;
      }
      if (!data.getRoom) {
        console.error('Room data is missing from query response');
        return;
      }
      if (data.getRoom && data.getRoomMessageCount) {
        const totalMessages = data.getRoomMessageCount;
        const currentMessages = data.getRoom.messages?.length || 0;
        const totalLoaded = offset + currentMessages;
        setHasMore(totalLoaded < totalMessages);
      }
    },
    onError: (error) => {
      console.error('Error loading room:', error.message);
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: false,
    pollInterval: 0,
    nextFetchPolicy: 'network-only'
  });

  const handleLoadRoom = useCallback(async (roomId: string) => {
    setOffset(0);

    try {
      const roomResponse = await loadRoom({
        variables: {
          roomId: Number(roomId),
          limit: 100,
          offset: 0
        }
      });

      // Start Apollo's built-in polling for real-time updates
      startPolling(1000); // Poll every 1 second for immediate updates

      if (roomResponse.data?.getRoom?.messages?.length > 0) {
        // Track the latest message ID for reference
        latestMessageIdRef.current = roomResponse.data.getRoom.messages[0].id;

        // Mark unread messages as read
        const unreadMessageIds = roomResponse.data.getRoom.messages
          .filter(m => !m.readAt)
          .map(m => Number(m.id));
        if (unreadMessageIds.length > 0) {
          await updateReadAtMessages({
            variables: {
              messageIds: unreadMessageIds
            }
          });
        }
      }
      return roomResponse;
    } catch (error) {
      console.error('Error in handleLoadRoom:', error);
      throw error;
    }
  }, [loadRoom, updateReadAtMessages, startPolling]);

  const loadMoreMessages = useCallback(async (roomId: string) => {
    const newOffset = offset + MESSAGES_PER_PAGE;
    try {
      const response = await fetchMore({
        variables: {
          roomId: Number(roomId),
          limit: MESSAGES_PER_PAGE,
          offset: newOffset
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult || !fetchMoreResult.getRoom?.messages?.length) {
            return prev;
          }

          // Merge new messages with existing ones, avoiding duplicates
          const existingMessages = prev.getRoom?.messages || [];
          const newMessages = fetchMoreResult.getRoom.messages;
          const existingIds = new Set(existingMessages.map(msg => msg.id));
          const uniqueNewMessages = newMessages.filter(msg => !existingIds.has(msg.id));

          return {
            ...prev,
            getRoom: {
              ...prev.getRoom,
              messages: [...existingMessages, ...uniqueNewMessages]
            }
          };
        }
      });

      setOffset(newOffset);
      return response;
    } catch (error) {
      console.error('Error loading more messages:', error);
      throw error;
    }
  }, [offset, fetchMore]);

  /**
   * Creates messages inside an existing room.
   * After creating the message, push the new message in the current Room's messages array cache.
   */
  const handleCreateMessage = useCallback(async (roomId: number, content: string, imageId?: number) => {
    try {
      const result = await createMessage({
        variables: {
          content,
          roomId: Number(roomId),
          imageId: imageId ? Number(imageId) : undefined
        }
      });

      // Update local state immediately
      if (result.data?.createMessage) {
        // Update the latest message ID reference
        latestMessageIdRef.current = result.data.createMessage.id;
      }

      return result;
    } catch (e) {
      console.error('Error creating message:', e);
      throw new Error(e.message);
    }
  }, [createMessage]);

  /**
   * Updates each messages once it is viewed.
   * Only update readAt dates if there is no existing data.
   */
  const handleReadAtMessages = useCallback(async (messageIds: number[], roomId: number) => {
    await updateReadAtMessages({
      update: (cache: InMemoryCache, { data: readAtData }) => {
        if (readAtData && readAtData.updateReadAtMessages.length === 0) {
          return;
        }

        const query = {
          query: GET_ROOM,
          variables: { roomId, limit: MESSAGES_PER_PAGE, offset }
        };
        const existingRoom = cache.readQuery<IGetRoom>(query);
        if (existingRoom?.getRoom) {
          const updatedRoom = {
            ...existingRoom,
            getRoom: {
              ...existingRoom.getRoom,
              hasUnreadMessage: false,
              messages: existingRoom.getRoom.messages.map(msg => ({
                ...msg,
                readAt: messageIds.includes(Number(msg.id)) ? new Date().toISOString() : msg.readAt
              }))
            }
          };

          cache.writeQuery({
            query: GET_ROOM,
            variables: { roomId, limit: MESSAGES_PER_PAGE, offset },
            data: updatedRoom
          });
        }
      },
      variables: {
        messageIds
      }
    }).catch((e) => {
      throw new Error(e.message);
    });
  }, [updateReadAtMessages, offset]);

  const getOtherUser = useCallback((
    members: GET_PAGINATED_ROOMS_getPaginatedRooms_members[]
  ) => {
    const otherUser = members.find((m) => m.id !== props.currentUser.id);
    return otherUser;
  }, [props.currentUser.id]);

  /**
   * When selecting user, the API searches for an existing single room types.
   * If it does not exist, it creates a new one. Otherwise, it redirects the page to the existing chatroom URL.
   */
  const handleSelectOtherUser = useCallback(async (user: GET_CURRENT_USER_me) => {
    try {
      setSelectedOtherUser(user);
      await hasSingleChatHistory({
        variables: {
          otherUserId: parseInt(user.id, 10)
        }
      });
    } catch (e) {
      throw new Error(e.message);
    }
  }, [hasSingleChatHistory]);

  /**
   *
   * After creating the room between the other user, this function redirects the page to the newly created chatroom.
   *
   * @param memberIds - other user ID
   * @param type - currently only supports 'single' rooms
   */
  const handleCreateRoom = useCallback(async (memberIds: number[], type = 'single') => {
    await createRoom({
      update: (cache: InMemoryCache, { data: rData }) => {
        const paginatedRoomQuery = {
          query: GET_PAGINATED_ROOMS,
          variables: {
            limit: roomsPerPage,
            page: currentPage
          }
        };
        const cacheData =
          cache.readQuery<GET_PAGINATED_ROOMS_RESPONSE>(paginatedRoomQuery);

        // Adding new data to the current room's messages array
        if (cacheData && rData?.createChatRoom) {
          const createdRoom =
            rData.createChatRoom as CREATE_ROOM_createChatRoom;

          const updatedRooms = [
            {
              ...createdRoom,
              hasUnreadMessage: false,
              recentMessage: null
            },
            ...cacheData.getPaginatedRooms.filter((r) => !!r.recentMessage)
          ];
          cache.writeQuery({
            data: {
              ...cacheData,
              getPaginatedRooms: updatedRooms
            },
            ...paginatedRoomQuery
          });
        }
        return redirectToRoom(rData.createChatRoom.id);
      },
      variables: {
        memberIds,
        roomType: type
      }
    });
  }, [createRoom, roomsPerPage, currentPage, history]);

  /**
   * Creates a coffee chat room between users.
   * This creates a room with chatType: 'coffee' and sends the initial request message.
   *
   * @param memberIds - other user ID array
   */
  const handleCreateCoffeeChatRoom = useCallback(async (memberIds: number[]) => {
    try {
      const result = await createRoom({
        update: (cache: InMemoryCache, { data: rData }) => {
          const paginatedRoomQuery = {
            query: GET_PAGINATED_ROOMS,
            variables: {
              limit: roomsPerPage,
              page: currentPage
            }
          };
          const cacheData =
            cache.readQuery<GET_PAGINATED_ROOMS_RESPONSE>(paginatedRoomQuery);

          // Adding new data to the current room's messages array
          if (cacheData && rData?.createChatRoom) {
            const createdRoom =
              rData.createChatRoom as CREATE_ROOM_createChatRoom;

            const updatedRooms = [
              {
                ...createdRoom,
                hasUnreadMessage: false,
                recentMessage: null
              },
              ...cacheData.getPaginatedRooms.filter((r) => !!r.recentMessage)
            ];
            cache.writeQuery({
              data: {
                ...cacheData,
                getPaginatedRooms: updatedRooms
              },
              ...paginatedRoomQuery
            });
          }
        },
        variables: {
          memberIds,
          roomType: 'single',
          chatType: 'coffee'
        }
      });

      // After room is created, send the coffee chat request message
      if (result.data?.createChatRoom) {
        const roomId = result.data.createChatRoom.id;
        console.log('Coffee chat room created, sending request message, roomId:', roomId);

        // Send the coffee chat request message
        await handleCreateMessage(Number(roomId), 'Requesting for coffee chat!');

        // Redirect to the room
        return redirectToRoom(Number(roomId));
      }
    } catch (error) {
      console.error('Error creating coffee chat room:', error);
      throw error;
    }
  }, [createRoom, roomsPerPage, currentPage, history, handleCreateMessage]);

  const redirectToRoom = useCallback((roomId: number) =>
    history.push(`/messages/${roomId}`), [history]);

  /**
   * Accepts a coffee chat request by updating the room's isAccepted field
   */
  const handleAcceptCoffeeChat = useCallback(async (roomId: string) => {
    try {
      console.log('Person B accepting coffee chat for roomId:', roomId);

      // Update the room's isAccepted field
      await updateCoffeeChatAcceptance({
        variables: {
          roomId: Number(roomId),
          isAccepted: true
        }
      });

      // Send a system message to indicate acceptance
      await handleCreateMessage(Number(roomId), 'Great to be matched with you. Looking forward to our coffee chat!');

      return true;
    } catch (error) {
      console.error('Error accepting coffee chat:', error);
      return false;
    }
  }, [updateCoffeeChatAcceptance, handleCreateMessage]);

  /**
   * Explicit declaration of the context provider value
   *
   * API Descriptions:
   *
   * actions - list of functions to manipulate the messageContextProvider states
   * currentRoom - Current chatroom information
   * currentUser - Current logged-in user information
   * singleChatHistory - Lazy literal response data of the HAS_SINGLE_CHAT_HISTORY query
   */
  const contextProviderValues = useMemo(() => ({
    actions: {
      getOtherUser,
      handleCreateMessage,
      handleCreateRoom,
      handleCreateCoffeeChatRoom,
      handleLoadRoom,
      handleReadAtMessages,
      handleSelectOtherUser,
      loadMoreMessages,
      setHasMore,
      stopPolling,
      handleAcceptCoffeeChat
    },
    currentRoom: {
      called: roomCalled,
      data: roomData,
      error: roomError,
      loading: roomLoading,
      hasMore,
      offset
    },
    currentUser: props.currentUser,
    singleChatHistory: {
      called: singleChatHistoryCalled,
      data: singleChatHistoryData,
      error: singleChatHistoryError,
      loading: singleChatHistoryLoading
    }
  }), [
    getOtherUser,
    handleCreateMessage,
    handleCreateRoom,
    handleCreateCoffeeChatRoom,
    handleLoadRoom,
    handleReadAtMessages,
    handleSelectOtherUser,
    loadMoreMessages,
    setHasMore,
    stopPolling,
    fetchMore,
    roomCalled,
    roomData,
    roomError,
    roomLoading,
    hasMore,
    offset,
    props.currentUser,
    singleChatHistoryCalled,
    singleChatHistoryData,
    singleChatHistoryError,
    singleChatHistoryLoading,
    handleAcceptCoffeeChat
  ]);

  return (
    <MessageContext.Provider value={contextProviderValues}>
      {props.children}
    </MessageContext.Provider>
  );
};
